/**
 * Email-first discovery: which workspaces sign this address in through an
 * identity provider?
 *
 * The login page asks this to decide which "Sign in with …" buttons to render
 * and whether the password box stays. The human picks from a list the server
 * built (spec V2, V9) — with ONE exception, `autoRedirectTarget`: when exactly
 * one workspace matches AND it requires SSO there is no choice left to make,
 * so Continue goes straight to the IdP.
 *
 * 🔴 This is a CONVENIENCE lookup in front of the only way into the product. If
 * it fails — network, 500, garbage response — the page must fall back to the
 * password field. An outage in "which login form should I show?" must never
 * become an outage in "can anyone log in at all?".
 */

export type SsoWorkspace = {
  slug: string;
  name: string;
  /** SSO is the ONLY way into this workspace (its `require_sso` flag). */
  required: boolean;
  startUrl: string;
};

export type DiscoveryOutcome =
  /** No IdP for this domain: show the password field, exactly as before. */
  | { kind: "password" }
  /** One or more workspaces federate this domain. */
  | { kind: "sso"; workspaces: SsoWorkspace[]; allRequired: boolean };

export async function discoverSso(email: string): Promise<DiscoveryOutcome> {
  const trimmed = (email ?? "").trim();
  if (!trimmed.includes("@")) return { kind: "password" };

  try {
    const response = await fetch("/api/auth/oidc/discover", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: trimmed }),
    });
    if (!response.ok) return { kind: "password" };

    const body = await response.json();
    const raw: unknown[] = Array.isArray(body?.workspaces) ? body.workspaces : [];
    if (!body?.sso || raw.length === 0) return { kind: "password" };

    const workspaces: SsoWorkspace[] = raw
      .filter((w): w is Record<string, unknown> => !!w && typeof w === "object")
      .map((w) => ({
        slug: String(w.slug ?? ""),
        name: String(w.name ?? ""),
        required: Boolean(w.required),
        startUrl: String(w.start_url ?? ""),
      }))
      .filter((w) => w.slug && w.startUrl);
    if (workspaces.length === 0) return { kind: "password" };

    return {
      kind: "sso",
      workspaces,
      // 🔴 Only take the password box away when EVERY workspace that could
      // apply has asked for that. We do not know which one she belongs to —
      // and she may belong to none of them, with only a personal workspace.
      allRequired: workspaces.every((w) => w.required),
    };
  } catch {
    // Deliberately swallowed — see the note at the top. A thrown error here
    // would leave the user staring at a form that does nothing.
    return { kind: "password" };
  }
}

/** The typed address rides along as a hint, so the IdP prefills or selects
 *  that account instead of silently reusing whatever session it already has.
 *  A hint only: who the person is still comes from the IdP's signed token. */
export function withLoginHint(startUrl: string, email: string): string {
  const trimmed = (email ?? "").trim();
  if (!trimmed.includes("@")) return startUrl;
  return `${startUrl}${startUrl.includes("?") ? "&" : "?"}login_hint=${encodeURIComponent(trimmed)}`;
}

/**
 * Where Continue should go on its own, or null to show the buttons.
 *
 * Only when exactly ONE workspace federates the domain and that workspace
 * REQUIRES SSO: there is nothing for the human to choose, and the password
 * box would be hidden anyway. With several matches, or with SSO merely
 * offered, the buttons (and the password field) stay.
 *
 * Two doors keep the password path reachable (owners are exempt from the
 * requirement at the workspace boundary, so it must be reachable):
 *   - `passwordDoor`: the page was opened at /login/password — never redirect.
 *   - `afterSsoError`: the IdP just bounced us back with an error — show the
 *     buttons rather than sending the person straight back into the failure.
 */
export function autoRedirectTarget(
  outcome: DiscoveryOutcome,
  email: string,
  opts: { passwordDoor?: boolean; afterSsoError?: boolean } = {},
): string | null {
  if (opts.passwordDoor || opts.afterSsoError) return null;
  if (outcome.kind !== "sso") return null;
  if (outcome.workspaces.length !== 1) return null;
  const only = outcome.workspaces[0];
  if (!only.required) return null;
  return withLoginHint(only.startUrl, email);
}
